import { useToggle } from "@mantine/hooks";
import { findWhere } from "underscore";
import classNames from "classnames";
import { Button } from "reactstrap";
import { useState } from "react";
import { useMemo } from "react";

import SummaryCardView from "../../../components/Widgets/SummaryCardWidget/SummaryCardView";
import TabbedMultiSubitem from "../../../components/Records/Subitems/TabbedMultiSubitem";
import MultiSubitemEditor from "../../Records/Subitems/MultiSubitemEditor";
import { generatePrefill } from "../../../utils/QuickCreateUtils";
import useRecord from "../../../api/api-hooks/records/useRecord";
import SkeletonLoader from "../../../elements/SkeletonLoader";
import useUser from "../../../api/api-hooks/auth/useUser";
import useModule from "../../../hooks/useModule";
import StrUtils from "../../../utils/StrUtils";
import AppDrawer from "../AppDrawer";

/**
 * SubitemDrawer - Drawer used for creating / editing Subitems.
 * Leave this comment until this is converted to TypeScript.
 * @param {*}
 * @returns
 */
const SubitemDrawer = ({
  open,
  existingRecord,
  recordID, // NOT REQUIRED, only required when existingRecord is not provided.
  subitemID, // NOT REQUIRED, only required when existingRecord is not provided. (For multiSubitem)
  field,
  onClose,
  onExited,
  displayMode,
  moduleAPIName,
  index, // For using an individual MultiSubitem in SummaryCard display mode.
  reusableViewReferenceFields,
}) => {
  const [addingSubitem, setAddingSubitem] = useState(false);
  const [isDuplicating, toggleDuplicateSubitem] = useToggle();
  const [prefill, setPrefill] = useState(null);
  const isMultiSubitem = field?.FieldType === "MultiSubitem";
  const { getModule } = useModule();
  const { user } = useUser();
  const module = getModule(moduleAPIName);
  // Fetch foundRecord when existingRecord is not provided.
  const { record: foundRecord, recordLoading } = useRecord({
    moduleAPIName,
    recordID,
    enabled: !!recordID,
  });

  const subitem = useMemo(() => {
    if (!recordID) {
      const subitem = existingRecord?.[field?.APIName];

      if (typeof index === "number") return subitem[index];

      return existingRecord?.[field?.APIName];
    }

    if (recordLoading) return null;
    if (!foundRecord) return null;

    if (subitemID) {
      const subitemField = foundRecord[field?.APIName];

      return Array.isArray(subitemField)
        ? findWhere(subitemField, { id: subitemID })
        : subitemField;
    }

    if (typeof index === "number") {
      return foundRecord[field?.APIName][index];
    }

    return foundRecord[field?.APIName];
  }, [
    foundRecord,
    existingRecord,
    recordLoading,
    subitemID,
    field?.APIName,
    recordID,
    index,
  ]);

  const record = foundRecord || existingRecord;

  return (
    <>
      <AppDrawer
        open={open}
        placement="bottom"
        size="lg"
        onClose={onClose}
        onExited={onExited}
        headerTitle={field?.Name ?? "Subitem Drawer"}
        bodyCSS={classNames(
          "m-0 px-0 pb-8",
          { "pt-3": isMultiSubitem },
          { "pt-0": !isMultiSubitem }
        )}
        headerOptions={
          <>
            {addingSubitem || isDuplicating ? (
              <Button
                size="sm"
                onClick={() => {
                  if (addingSubitem) setAddingSubitem(false);
                  if (isDuplicating) toggleDuplicateSubitem();
                }}
              >
                Done
              </Button>
            ) : null}
          </>
        }
        body={
          <>
            {recordLoading ? (
              <div className="d-flex w-100">
                <div className="w-50">
                  <SkeletonLoader css="my-4 mx-2" />
                  <SkeletonLoader css="my-4 mx-2" />
                  <SkeletonLoader css="my-4 mx-2" />
                  <SkeletonLoader css="my-4 mx-2" />
                </div>
                <div style={{ width: 30 }}></div>
                <div className="w-50">
                  <SkeletonLoader css="my-4 mx-2" />
                  <SkeletonLoader css="my-4 mx-2" />
                  <SkeletonLoader css="my-4 mx-2" />
                  <SkeletonLoader css="my-4 mx-2" />
                </div>
              </div>
            ) : (
              <>
                {displayMode === "SummaryCard" ? (
                  <SummaryCardView
                    collapse={false}
                    fieldSchema={field}
                    fields={field?.SubitemFields}
                    isReference={false}
                    maxHeight={"unset"}
                    parentRecord={record}
                    record={subitem}
                    useTitle={false}
                    readOnly={false}
                    module={module}
                    moduleAPIName={moduleAPIName}
                    reusableViewReferenceFields={reusableViewReferenceFields}
                  />
                ) : (
                  <>
                    {addingSubitem || isDuplicating ? (
                      <>
                        <MultiSubitemEditor
                          isDuplicating={isDuplicating}
                          toggleDuplicateSubitem={toggleDuplicateSubitem}
                          module={module}
                          parentRecord={record}
                          fieldSchema={field}
                          prefill={prefill}
                          setPrefill={setPrefill}
                          setSubitemAdding={setAddingSubitem}
                          reusableViewReferenceFields={
                            reusableViewReferenceFields
                          }
                        />
                      </>
                    ) : (
                      <TabbedMultiSubitem
                        subitemDuplicating={isDuplicating}
                        toggleDuplicateSubitem={toggleDuplicateSubitem}
                        setAddingSubitem={setAddingSubitem}
                        module={module}
                        fieldSchema={field}
                        records={record?.[field?.APIName]}
                        parentRecord={record}
                        showTitle={false}
                        managePrefill={(prefill) =>
                          managePrefill(prefill, moduleAPIName)
                        }
                        reusableViewReferenceFields={
                          reusableViewReferenceFields
                        }
                      />
                    )}
                  </>
                )}
              </>
            )}
          </>
        }
      />
    </>
  );

  function managePrefill(value, moduleAPIName) {
    const variableVals = {
      user: {
        id: user?.id,
        Name: user?.Name,
        _id: user?._id,
        _RecordName: user?.Name,
        FirstName: StrUtils.getFirstName(user?.Name),
        LastName: StrUtils.getLastName(user?.Name),
        /** For Feedback, to know who send the message. */
        _ModuleAPIName: moduleAPIName === "Feedback" ? "People" : "Contacts",
      },
      record: existingRecord || foundRecord,
    };
    const prefill = value.PrefillPairs;
    generatePrefill(prefill, variableVals, field.SubitemFields, setPrefill);
  }
};

export default SubitemDrawer;
